import React, { useEffect, useState } from 'react';
import {
  ReactiveBase,
  DataSearch,
  ReactiveList,
  ReactiveComponent,
  MultiList,
  StateProvider,
} from '@appbaseio/reactivesearch';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { CheckboxList } from '../../components/elasticsearch/CheckboxList';
import { Bookmark } from '../../components/ui/icon/Bookmark';
import reactivesearchTheme from '../../reactivesearchTheme';
import {
  useAddBookmarkMutation,
  useGetBookmarkedElementIdsQuery,
  useRemoveBookmarkMutation,
} from '../../redux-modules/service';

import './artworks-catalog.css';

const timestamp = window.localStorage
  ? window.localStorage.getItem('artwork-catalog-seed') || +(new Date()) // Fallback to current time if null
  : +(new Date());

if (window.localStorage) {
  window.localStorage.setItem('artwork-catalog-seed', timestamp);
}

const newTypography = { ...reactivesearchTheme.typography, ...{ fontSize: 'inherit' } };
const newReactivesearchTheme = { ...reactivesearchTheme, ...{ typography: newTypography } };

const sortOptions = [
  {
    label: 'Best Match',
    // query: { sort: [{ _score: { order: 'desc' } }, { 'title.keyword': { order: 'asc' } }] },
    query: { query: { function_score: { functions: [{ random_score: { seed: timestamp } }] } } },
  },
  {
    label: 'Title A-Z',
    query: { sort: [{ 'title.keyword': { order: 'asc' } }] },
  },
  {
    label: 'Title Z-A',
    query: { sort: [{ 'title.keyword': { order: 'desc' } }] },
  },
  {
    label: 'Artist A-Z',
    query: { sort: [{ 'primaryArtistName.keyword': { order: 'asc' } }] },
  },
  {
    label: 'Artist Z-A',
    query: { sort: [{ 'primaryArtistName.keyword': { order: 'desc' } }] },
  },
  {
    label: 'Gallery A-Z',
    query: { sort: [{ 'account.shortName.keyword': { order: 'asc' } }] },
  },
  {
    label: 'Gallery Z-A',
    query: { sort: [{ 'account.shortName.keyword': { order: 'desc' } }] },
  },
];

function ArtworksCatalog({
  url,
  credentials,
  app,
  fair,
  fairCurrency,
  showFilters,
}) {
  const wishlistTypeHandle = 'bookmarkedArtworks';
  const [sort, setSort] = useState(sortOptions[0]);
  const [bookmarksOnly, setBookmarksOnly] = useState(false);
  const [handleAddBookmark] = useAddBookmarkMutation();
  const [handleRemoveBookmark] = useRemoveBookmarkMutation();
  const {
    data: bookmarks,
    isLoading: isLoadingBookmarks,
  } = useGetBookmarkedElementIdsQuery(wishlistTypeHandle /* , { skip: !bookmarksOnly } */);

  useEffect(() => {
    window.initCustomForms();
    window.initMobileNavFilters();
  }, []);

  return (
    <ReactiveBase
      url={url}
      credentials={credentials}
      app={app}
      enableAppbase
      theme={newReactivesearchTheme}
    >
      <div className="filters-section">
        <div className="container">
          <div className="filters-section-inner ajax-filters-container-paginated">
            <div className="filter-box">
              <fieldset>
                <div className="filter-section-holder">
                  <div className="filters-wrap-inner filters-wrap-inner_main">
                    <h2 className="h1 title-md">Artworks</h2>
                    <span className="result">
                      <StateProvider
                        componentIds="page"
                        strict={false}
                        render={({ searchState }) => {
                          const { hits } = searchState.page;
                          if (!hits) {
                            return null;
                          }

                          const { total } = hits;
                          return total === 1
                            ? `Showing ${total} artwork`
                            : `Showing ${total} artworks`;
                        }}
                      />
                    </span>
                  </div>
                  <hr />
                  <div className="filters-wrap filters-wrap--filters" id="filters-wrap--artworks">
                    <div className="filters-inner-holder d-lg-flex justify-content-lg-between">
                      {showFilters !== 'false' ? (
                        <button type="button" className="filters-main-opener filters-btn text-uppercase p-0">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path d="M0 416C0 407.2 7.164 400 16 400H81.6C89.01 363.5 121.3 336 160 336C198.7 336 230.1 363.5 238.4 400H496C504.8 400 512 407.2 512 416C512 424.8 504.8 432 496 432H238.4C230.1 468.5 198.7 496 160 496C121.3 496 89.01 468.5 81.6 432H16C7.164 432 0 424.8 0 416V416zM208 416C208 389.5 186.5 368 160 368C133.5 368 112 389.5 112 416C112 442.5 133.5 464 160 464C186.5 464 208 442.5 208 416zM352 176C390.7 176 422.1 203.5 430.4 240H496C504.8 240 512 247.2 512 256C512 264.8 504.8 272 496 272H430.4C422.1 308.5 390.7 336 352 336C313.3 336 281 308.5 273.6 272H16C7.164 272 0 264.8 0 256C0 247.2 7.164 240 16 240H273.6C281 203.5 313.3 176 352 176zM400 256C400 229.5 378.5 208 352 208C325.5 208 304 229.5 304 256C304 282.5 325.5 304 352 304C378.5 304 400 282.5 400 256zM496 80C504.8 80 512 87.16 512 96C512 104.8 504.8 112 496 112H270.4C262.1 148.5 230.7 176 192 176C153.3 176 121 148.5 113.6 112H16C7.164 112 0 104.8 0 96C0 87.16 7.164 80 16 80H113.6C121 43.48 153.3 16 192 16C230.7 16 262.1 43.48 270.4 80H496zM144 96C144 122.5 165.5 144 192 144C218.5 144 240 122.5 240 96C240 69.49 218.5 48 192 48C165.5 48 144 69.49 144 96z" />
                          </svg>
                          Filter
                          <StateProvider
                            componentIds={['category', 'priceBracket', 'period', 'collection']}
                            strict={false}
                            render={({ searchState }) => {
                              const countCategory = _.size(searchState.category.value);
                              const countCollection = _.size(searchState.collection.value);
                              const countPriceBracket = _.size(searchState.priceBracket.value);
                              const countPeriod = _.size(searchState.period.value);
                              const result = countCategory
                                + countPriceBracket
                                + countPeriod
                                + countCollection;
                              return result > 0
                                ? (<span className="counter-filters">{result}</span>)
                                : null;
                            }}
                          />
                        </button>
                      ) : <span /> }
                      <div className="filters-wrap-inner">
                        <div className="select-filter select-sort flex-grow-1">
                          <select onChange={(e) => setSort(sortOptions[e.target.value])} data-jcf='{"wrapNative": false, "wrapNativeOnMobile": false}'>
                            {sortOptions.map((opt, index) => (
                              <option key={opt.label} value={index}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {!isLoadingBookmarks ? (
                          <div className="bookmarks-check" title="Show bookmarked items only.">
                            <input
                              id="checkbox_bookmark"
                              type="checkbox"
                              defaultChecked={bookmarksOnly}
                              onChange={(event) => setBookmarksOnly(event.target.checked)}
                            />
                            <label htmlFor="checkbox_bookmark">
                              <Bookmark className="bookmark-icon" />
                              <Bookmark solid className="bookmark-icon bookmark-icon_active" />
                              <span>Bookmarks</span>
                            </label>
                          </div>
                        ) : (
                          <div className="ff-apercu small">Loading...</div>
                        )}
                        <ReactiveComponent
                          componentId="bookmarks"
                          customQuery={() => {
                            if (bookmarksOnly && bookmarks && !isLoadingBookmarks) {
                              return {
                                query: {
                                  bool: {
                                    must: [
                                      {
                                        terms: {
                                          id: bookmarks.items,
                                        },
                                      },
                                    ],
                                  },
                                },
                              };
                            }
                            return {};
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="filters-content-drop">
                  <button type="button" className="drop-heading filters-main-opener w-100">
                    <span className="title">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path d="M0 416C0 407.2 7.164 400 16 400H81.6C89.01 363.5 121.3 336 160 336C198.7 336 230.1 363.5 238.4 400H496C504.8 400 512 407.2 512 416C512 424.8 504.8 432 496 432H238.4C230.1 468.5 198.7 496 160 496C121.3 496 89.01 468.5 81.6 432H16C7.164 432 0 424.8 0 416V416zM208 416C208 389.5 186.5 368 160 368C133.5 368 112 389.5 112 416C112 442.5 133.5 464 160 464C186.5 464 208 442.5 208 416zM352 176C390.7 176 422.1 203.5 430.4 240H496C504.8 240 512 247.2 512 256C512 264.8 504.8 272 496 272H430.4C422.1 308.5 390.7 336 352 336C313.3 336 281 308.5 273.6 272H16C7.164 272 0 264.8 0 256C0 247.2 7.164 240 16 240H273.6C281 203.5 313.3 176 352 176zM400 256C400 229.5 378.5 208 352 208C325.5 208 304 229.5 304 256C304 282.5 325.5 304 352 304C378.5 304 400 282.5 400 256zM496 80C504.8 80 512 87.16 512 96C512 104.8 504.8 112 496 112H270.4C262.1 148.5 230.7 176 192 176C153.3 176 121 148.5 113.6 112H16C7.164 112 0 104.8 0 96C0 87.16 7.164 80 16 80H113.6C121 43.48 153.3 16 192 16C230.7 16 262.1 43.48 270.4 80H496zM144 96C144 122.5 165.5 144 192 144C218.5 144 240 122.5 240 96C240 69.49 218.5 48 192 48C165.5 48 144 69.49 144 96z" />
                      </svg>
                      <span>FILTERS</span>
                    </span>
                    <span className="icon-close">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                        <path d="M315.3 411.3c-6.253 6.253-16.37 6.253-22.63 0L160 278.6l-132.7 132.7c-6.253 6.253-16.37 6.253-22.63 0c-6.253-6.253-6.253-16.37 0-22.63L137.4 256L4.69 123.3c-6.253-6.253-6.253-16.37 0-22.63c6.253-6.253 16.37-6.253 22.63 0L160 233.4l132.7-132.7c6.253-6.253 16.37-6.253 22.63 0c6.253 6.253 6.253 16.37 0 22.63L182.6 256l132.7 132.7C321.6 394.9 321.6 405.1 315.3 411.3z" />
                      </svg>
                    </span>
                  </button>
                  <div className="filters-drop-inner-holder">
                    <div className="filters-drop-inner">
                      <div className="search-filter">
                        <DataSearch
                          innerClass={{
                            input: 'form-control',
                            list: 'gallery-title__list',
                          }}
                          componentId="q"
                          dataField={[
                            'title',
                            'primaryArtistName',
                            'account.title',
                          ]}
                          iconPosition="right"
                          filterLabel="Search term"
                          placeholder="Search"
                          react={{ and: ['fair', 'category', 'priceBracket', 'period', 'collection'] }}
                          URLParams
                          // searchOperators
                          fuzziness="AUTO"
                        />
                      </div>
                      <ul className="filters-list">
                        <li>
                          {fair ? (
                            <ReactiveComponent
                              componentId="fair"
                              customQuery={() => ({
                                query: {
                                  bool: {
                                    must: [
                                      {
                                        match: {
                                          'fair.title.keyword': fair,
                                        },
                                      },
                                    ],
                                  },
                                },
                              })}
                            />
                          ) : (
                            <MultiList
                              componentId="fair"
                              dataField="fair.title.keyword"
                              filterLabel="Fair"
                              showCheckbox
                              react={{ and: ['q', 'category', 'priceBracket', 'period', 'collection'] }}
                              URLParams
                              showSearch={false}
                            >
                              {
                                ({
                                  data,
                                  value,
                                  handleChange,
                                }) => (
                                  <CheckboxList
                                    id="fair"
                                    title="Fair"
                                    size={5}
                                    data={data}
                                    handleChange={handleChange}
                                    value={value}
                                  />
                                )
                              }
                            </MultiList>
                          )}
                        </li>
                        <li>
                          <MultiList
                            componentId="category"
                            dataField="categories.title.keyword"
                            filterLabel="Category"
                            showCheckbox
                            react={{ and: ['q', 'fair', 'priceBracket', 'period', 'collection'] }}
                            URLParams
                            showSearch={false}
                            size={100}
                            aggregationSize={100}
                            sortBy="asc"
                          >
                            {
                              ({
                                data,
                                value,
                                handleChange,
                              }) => (
                                <CheckboxList
                                  id="category"
                                  title="Category"
                                  size={10}
                                  data={data}
                                  handleChange={handleChange}
                                  value={value}
                                />
                              )
                            }
                          </MultiList>
                        </li>
                        <li>
                          <MultiList
                            componentId="collection"
                            dataField="collections.title.keyword"
                            filterLabel="Collections"
                            showCheckbox
                            react={{ and: ['q', 'fair', 'priceBracket', 'period', 'category'] }}
                            URLParams
                            showSearch={false}
                            size={100}
                            aggregationSize={100}
                            sortBy="asc"
                          >
                            {
                              ({
                                data,
                                value,
                                handleChange,
                              }) => (
                                <CheckboxList
                                  id="collection"
                                  title="Collection"
                                  size={10}
                                  data={data}
                                  handleChange={handleChange}
                                  value={value}
                                />
                              )
                            }
                          </MultiList>
                        </li>
                        <li>
                          <MultiList
                            componentId="priceBracket"
                            dataField="priceBracket.title.keyword"
                            title=""
                            filterLabel="Price Bracket"
                            showCheckbox
                            react={{ and: ['q', 'fair', 'category', 'period', 'collection'] }}
                            URLParams
                            showSearch={false}
                          >
                            {
                              ({
                                data,
                                value,
                                handleChange,
                              }) => (
                                <CheckboxList
                                  id="price-bracket"
                                  title="Price Bracket"
                                  size={10}
                                  data={data}
                                  handleChange={handleChange}
                                  value={value}
                                  order={[
                                    'Up to $50k',
                                    'Up to €50k',
                                    '$50-150k',
                                    '$150-300k',
                                    '$300-500k',
                                    '$500-750k',
                                    '$750k-1m',
                                    'Above $1m',
                                  ]}
                                  mapping={{
                                    'Up to $50k': fairCurrency === 'USD' ? 'Up to $50k' : 'Up to €50k',
                                    'Up to €50k': fairCurrency === 'USD' ? 'Up to $50k' : 'Up to €50k',
                                    '$50-150k': fairCurrency === 'USD' ? '$50-150k' : '€50-150k',
                                    '$150-300k': fairCurrency === 'USD' ? '$150-300k' : '€150-300k',
                                    '$300-500k': fairCurrency === 'USD' ? '$300-500k' : '€300-500k',
                                    '$500-750k': fairCurrency === 'USD' ? '$500-750k' : '€500-750k',
                                    '$750k-1m': fairCurrency === 'USD' ? '$750k-1m' : '€750k-1m',
                                    'Above $1m': fairCurrency === 'USD' ? 'Above $1m' : 'Above €1m',
                                  }}
                                />
                              )
                            }
                          </MultiList>
                        </li>
                        <li>
                          <MultiList
                            componentId="period"
                            dataField="period.title.keyword"
                            filterLabel="Period"
                            showCheckbox
                            react={{ and: ['q', 'fair', 'category', 'priceBracket', 'collection'] }}
                            URLParams
                            showSearch={false}
                          >
                            {
                              ({
                                data,
                                value,
                                handleChange,
                              }) => (
                                <CheckboxList
                                  id="period"
                                  title="Period"
                                  size={10}
                                  data={data}
                                  handleChange={handleChange}
                                  value={value}
                                  order={[
                                    'Up to 500 AD',
                                    '500 to 1400',
                                    '15th Century',
                                    '16th Century',
                                    '17th Century',
                                    '18th Century',
                                    '19th Century',
                                    '20th Century',
                                    '21st Century',
                                  ]}
                                />
                              )
                            }
                          </MultiList>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="btn-results-holder ff-apercu">
                    <button className="btn btn-dark btn-dark-force btn-results filters-main-opener" type="button">
                      Show results
                      <StateProvider
                        componentIds="page"
                        strict={false}
                        render={({ searchState }) => {
                          const { hits } = searchState.page;
                          if (!hits) {
                            return null;
                          }

                          const { total } = hits;
                          return ` (${total})`;
                        }}
                      />
                    </button>
                  </div>
                </div>
              </fieldset>
            </div>
            <hr className="d-lg-none" />
            <StateProvider
              strict={false}
              render={({ searchState }) => {
                const pageSize = 24;
                const hits = _.get(searchState, 'page.hits.total', 0);
                const numberOfPages = hits > 0 ? Math.ceil(hits / pageSize) : 0;

                return (
                  <ReactiveList
                    componentId="page"
                    innerClass={{ pagination: 'pagination reactive-pagination reactive-pagination--artworks' }}
                    dataField="id"
                    pagination
                    size={pageSize}
                    showEndPage={numberOfPages > 8}
                    pages={numberOfPages > 8 ? 8 : numberOfPages}
                    showResultStats={false}
                    scrollOnChange={false}
                    URLParams
                    react={{ and: ['q', 'fair', 'category', 'priceBracket', 'period', 'bookmarks', 'collection'] }}
                    defaultQuery={() => (sort.query)}
                    onData={() => {
                      window.scrollTo(0, 0);
                      window.jQuery('.js-flexmasonry-filter-grid').css('opacity', 0);
                      window.jQuery('.js-flexmasonry-filter-grid').each((i, grid) => {
                        window.jQuery(grid).imagesLoaded(() => {
                          window.jQuery('.flexmasonry-break').remove();
                          setTimeout(() => {
                            window.initFlexMasonry();
                            window.initGSAP();
                            window.initStickyScrollBlock();
                            window.initPinning('#filters-wrap--artworks');
                            window.jQuery('.js-flexmasonry-filter-grid').css('opacity', 1);
                          }, 200);
                        });
                      });
                    }}
                    renderNoResults={() => (
                      <div className="ff-apercu small">No Results found.</div>
                    )}
                    render={({ data }) => (
                      <div className="ajax-filter-holder">
                        <div className="ajax-filter-content js-flexmasonry-filter-grid js-flexmasonry-gsap">
                          {data.map((item) => {
                            const bookmarked = bookmarks
                              && bookmarks.items.indexOf(item.id) >= 0;
                            return (
                              /* eslint-disable-next-line no-underscore-dangle */
                              <div className="grid-item" key={item._id}>
                                <div className={`card-bookmark ${bookmarked ? 'bookmark-active' : ''}`}>
                                  <a href={item.url} className="card-image">
                                    <img
                                      src={_.get(
                                        item,
                                        'image.transforms.catalog.url',
                                        _.get(
                                          item,
                                          'image.url',
                                          null,
                                        ),
                                      )}
                                      alt={item.title}
                                    />
                                  </a>
                                  <div className="card-body">
                                    {!isLoadingBookmarks ? (
                                      <button
                                        type="button"
                                        className="bookmark"
                                        title={bookmarked ? 'Remove bookmark' : 'Add to bookmarks'}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (bookmarked) {
                                            handleRemoveBookmark({
                                              listType: wishlistTypeHandle,
                                              elementId: item.id,
                                            });
                                          } else {
                                            handleAddBookmark({
                                              listType: wishlistTypeHandle,
                                              elementId: item.id,
                                            });
                                          }
                                        }}
                                      >
                                        <Bookmark solid className="bookmark-icon bookmark-icon_active" />
                                        <Bookmark className="bookmark-icon" />
                                      </button>
                                    ) : ''}
                                  </div>
                                  <div className="card-text">
                                    <span className="subtitle text-uppercase">
                                      <a href={_.get(item, 'account.url', null)} className="artwork-catalog__artwork-title-exhibitor">
                                        {_.get(item, 'account.title', null)}
                                      </a>
                                    </span>
                                    <a href={item.url} className="artwork-catalog__artwork-title">
                                      <h3>
                                        {[item.attribution, item.primaryArtistName]
                                          .filter((artistInfo) => artistInfo)
                                          .join(' ')
                                          .trim()}
                                      </h3>
                                      <span className="card-title ff-portrait">
                                        <i>{item.title}</i>
                                        {item.year && (
                                          <>
                                            ,
                                            {' '}
                                            <span>{item.year}</span>
                                          </>
                                        )}
                                      </span>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  />
                );
              }}
            />
          </div>
        </div>
      </div>
    </ReactiveBase>
  );
}

ArtworksCatalog.propTypes = {
  url: PropTypes.string.isRequired,
  credentials: PropTypes.string.isRequired,
  app: PropTypes.string.isRequired,
  fair: PropTypes.string,
  fairCurrency: PropTypes.string,
  showFilters: PropTypes.string,
};

ArtworksCatalog.defaultProps = {
  fair: '',
  fairCurrency: 'USD',
  showFilters: 'true',
};

export { ArtworksCatalog };
